import { useEffect, useRef, useState, type ReactNode } from "react";
import { HOLD_REASONS, LABELS, holdText, type CyclePhase, type CycleState, type HoldReason, type SessionState } from "@match/shared";
import { Button, ErrorNote, Field, SidePanel, inputClass } from "@/components/ui";
import { api, errorMessage, type BurnPreview, type PropResetInput, type ResetPreview } from "@/lib/api";
import { dateTime, money, percent, sentence } from "@/lib/format";
import { useRefreshDirectory, useSession } from "@/lib/queries";
import { decimalInput } from "@/lib/units";
import type { PayoutStatus, SessionDetail } from "@/lib/types";

/**
 * Barra di avanzamento con etichetta e valori. `ratio` null = non applicabile.
 *
 * **Una forma sola per riga e per scheda**: `variant="row"` (quello che c'era,
 * dentro una cella o una lista) è il default; `variant="card"` aggiunge il
 * riquadro con titolo e valore sopra la barra — la stessa forma che prima
 * `Metric`/`Box` ripetevano una pagina alla volta.
 */
export function Progress({
  label,
  ratio,
  detail,
  tone = "neutral",
  approximate = false,
  approximateTitle,
  variant = "row",
  title,
  value,
  children,
}: {
  label: string;
  ratio: number | null;
  detail: string;
  tone?: "neutral" | "prop" | "broker" | "gain";
  /** Numero stimato: lo si dichiara, in modo discreto. */
  approximate?: boolean;
  /** Perché è una stima; default: la spiegazione del DD approssimato. */
  approximateTitle?: string;
  variant?: "row" | "card";
  /** Solo `variant="card"`: titolo del riquadro (es. «DD residuo»). */
  title?: string;
  /** Solo `variant="card"`: il valore grande sopra la barra. */
  value?: ReactNode;
  /** Solo `variant="card"`: note aggiuntive sotto la barra. */
  children?: ReactNode;
}) {
  const fill = { neutral: "bg-muted", prop: "bg-prop", broker: "bg-broker", gain: "bg-gain" }[tone];
  const bar = (
    <div className="min-w-0">
      <div className="flex items-baseline justify-between gap-2 text-xs">
        <span className="text-faint">
          {label}
          {approximate && <ApproximateNote title={approximateTitle} />}
        </span>
        <span className="truncate text-muted">{ratio === null ? "—" : detail}</span>
      </div>
      <div
        className="mt-1 h-1.5 overflow-hidden rounded-full bg-raised"
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={ratio === null ? undefined : Math.round(ratio * 100)}
      >
        {ratio !== null && <div className={`h-full rounded-full ${fill}`} style={{ width: `${Math.round(ratio * 100)}%` }} />}
      </div>
    </div>
  );
  if (variant !== "card") return bar;
  return (
    <div className="card p-4">
      {title && <p className="text-xs text-faint">{title}</p>}
      {value !== undefined && <p className="mt-0.5 text-xl font-semibold text-ink">{value}</p>}
      <div className="mt-2">
        {bar}
        {children}
      </div>
    </div>
  );
}

export const DD_APPROXIMATE_EXPLANATION =
  "Stima dal P&L chiuso del prop: il massimo raggiunto e il saldo a fine giornata, da cui con questa base dipende il DD reale, non sono ancora tracciati.";

/** Annotazione discreta, non un allarme: il numero è una stima. */
export function ApproximateNote({ title = DD_APPROXIMATE_EXPLANATION }: { title?: string }) {
  return (
    <span className="ml-1 text-faint" title={title}>
      · stima
    </span>
  );
}

const SESSION_STATE_TONE: Record<SessionState, string> = {
  ACTIVE: "border-gain/40 text-gain",
  PAUSED: "border-warn/40 text-warn",
  /*
   * In attesa di payout è uno STATO in cui non si opera, e si vede: il
   * periodo è chiuso, il tratto nuovo non è ancora nato, e chi apre
   * l'operazione può non essere chi ha chiesto il payout. Più marcato della
   * pausa proprio per non confondersi con lei.
   */
  AWAITING_PAYOUT: "border-warn/60 bg-warn/10 text-warn",
  // Il conto prop c'è, il broker no: stesso rilievo di AWAITING_PAYOUT, uno stato che non opera.
  AWAITING_BROKER: "border-warn/60 bg-warn/10 text-warn",
  COMPLETED: "border-line text-ink",
  FAILED: "border-line text-ink",
};

/** FAILED non è un guasto e CLOSED_PROFIT è l'esito che si cerca: i colori lo dicono. */
const CYCLE_STATE_TONE: Record<CycleState, string> = {
  ACTIVE: "text-ink",
  CLOSED_PROFIT: "text-gain",
  CLOSED_LOSS: "text-loss",
  ABANDONED: "text-muted",
};

/**
 * Lo stato di una Fase o di un Ciclo, in UNA faccia sola.
 *
 * "Conto bruciato" NON è rosso: nel modello Match è l'esito profittevole, e il
 * colore non deve suggerire un guasto — vale per entrambi i `kind`.
 *
 * Le due forme restano quelle che erano (`SessionStateLabel` era un badge con
 * bordo, `CycleStateLabel` un testo colorato senza bordo): unificare il
 * componente non vuol dire uniformare l'aspetto di due cose che nel resto
 * della pagina raccontano ruoli diversi — la Fase è un'operazione che si
 * segue, il Ciclo un esito che si legge.
 */
/**
 * Il payout: un incasso, quindi il PAGATO e' l'esito che si cerca (verde) e il
 * rifiutato e' l'unico che dice «non e' andata» (rosso). Richiesto e approvato
 * sono in attesa, e si leggono come tali.
 */
const PAYOUT_STATE: Record<PayoutStatus, { label: string; tone: string }> = {
  REQUESTED: { label: "Richiesto", tone: "text-warn" },
  APPROVED: { label: "Approvato", tone: "text-ink" },
  PAID: { label: "Pagato", tone: "text-gain" },
  REJECTED: { label: "Rifiutato", tone: "text-loss" },
};

export function StateBadge(props: { kind: "session"; state: SessionState } | { kind: "cycle"; state: CycleState } | { kind: "payout"; state: PayoutStatus }) {
  if (props.kind === "payout") {
    return <span className={PAYOUT_STATE[props.state].tone}>{PAYOUT_STATE[props.state].label}</span>;
  }
  if (props.kind === "session") {
    return <span className={`rounded border px-1.5 py-0.5 text-xs ${SESSION_STATE_TONE[props.state]}`}>{LABELS.sessionState[props.state]}</span>;
  }
  return <span className={CYCLE_STATE_TONE[props.state]}>{LABELS.cycleState[props.state]}</span>;
}

export const PHASE_LABEL = (p: CyclePhase) => LABELS.cyclePhase[p];

// ---------------------------------------------------------------------------
// Fase ferma: il motivo, il riquadro che lo chiede, il pulsante
// ---------------------------------------------------------------------------

/**
 * PERCHE' E' FERMA, E DA QUANDO.
 *
 * Il motivo sta accanto allo stato e non dentro un menu: «In pausa» da solo
 * fa aprire una scheda per scoprire una cosa che si sa gia'. Una Fase ferma
 * prima che il motivo esistesse dice «motivo non registrato» — non un motivo
 * inventato, e non il silenzio.
 */
export function HoldNote({ hold, className = "" }: { hold: { reason: HoldReason | null; note: string | null; since: string | null }; className?: string }) {
  return (
    <span className={`text-xs text-warn ${className}`} title={hold.note ?? undefined}>
      {holdText(hold)}
      {hold.since && <span className="text-faint"> · da {dateTime(hold.since)}</span>}
    </span>
  );
}

/**
 * IL RIQUADRO CHE CHIEDE IL MOTIVO, e non si chiude senza.
 *
 * I quattro motivi sono quelli che succedono davvero; «Altro» c'e' per non
 * far scegliere un motivo falso, e chi lo sceglie scrive che cosa sta
 * aspettando — li' la nota diventa obbligatoria, e il pulsante resta spento
 * finche' non c'e'.
 */
export function HoldPanel({ sessionId, onClose }: { sessionId: string; onClose: () => void }) {
  const refresh = useRefreshDirectory();
  const [reason, setReason] = useState<HoldReason>("KYC");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const manca = reason === "ALTRO" && !note.trim();

  const ferma = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.sessions.pause(sessionId, { reason, ...(note.trim() ? { note: note.trim() } : {}) });
      await refresh();
      onClose();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <SidePanel
      open
      title="Ferma la fase"
      subtitle="Finché è ferma non si apre niente. Le operazioni già a mercato si chiudono come sempre."
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            Annulla
          </Button>
          <Button variant="primary" onClick={() => void ferma()} disabled={busy || manca}>
            Ferma
          </Button>
        </>
      }
    >
      <fieldset>
        <legend className="mb-1.5 text-sm text-muted">Motivo</legend>
        <div className="space-y-1">
          {HOLD_REASONS.map((r) => (
            <label key={r} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-raised">
              <input type="radio" name="hold-reason" value={r} checked={reason === r} onChange={() => setReason(r)} />
              {LABELS.holdReason[r]}
            </label>
          ))}
        </div>
      </fieldset>
      <Field
        label={reason === "ALTRO" ? "Che cosa si aspetta" : "Nota (facoltativa)"}
        hint={reason === "ALTRO" ? "Con «Altro» il motivo si scrive: «altro» e basta non dice niente a chi legge fra un mese." : undefined}
      >
        {(id) => (
          <input
            id={id}
            className={inputClass}
            value={note}
            maxLength={500}
            onChange={(e) => setNote(e.target.value)}
            placeholder={reason === "ALTRO" ? "es. server della prop in manutenzione" : "es. documenti mandati il 20"}
          />
        )}
      </Field>
      {error && <ErrorNote>{error}</ErrorNote>}
    </SidePanel>
  );
}

/** Una voce di menu «…» che esegue (non porta a una pagina): la faccia di `MenuLink` e di «Crea ciclo». */
const MENU_ITEM_CLASS = "block w-full rounded px-2 py-1.5 text-left text-sm font-normal text-ink hover:bg-raised disabled:cursor-not-allowed disabled:text-faint";

/**
 * FERMA / RIPRENDI, un pulsante solo.
 *
 * Fermare passa dal riquadro — serve il motivo; riprendere no: il motivo c'è
 * già, e chiederne uno per ripartire sarebbe una domanda senza risposta.
 */
export function HoldButton({ session, size, asMenuItem = false }: { session: { id: string; state: SessionState }; size?: "sm"; /** Dentro un menu «…»: stessa faccia delle altre voci, non quella di un pulsante. */ asMenuItem?: boolean }) {
  const refresh = useRefreshDirectory();
  const [panel, setPanel] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const riprendi = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.sessions.resume(session.id);
      await refresh();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (asMenuItem) {
    return (
      <>
        <button
          type="button"
          role="menuitem"
          disabled={busy}
          title={error ?? undefined}
          onClick={() => (session.state === "PAUSED" ? void riprendi() : setPanel(true))}
          className={MENU_ITEM_CLASS}
        >
          {session.state === "PAUSED" ? "Riprendi" : "Pausa"}
        </button>
        {panel && <HoldPanel sessionId={session.id} onClose={() => setPanel(false)} />}
      </>
    );
  }

  return (
    <span title={error ?? undefined}>
      {session.state === "PAUSED" ? (
        <Button size={size} variant={size ? "ghost" : undefined} disabled={busy} onClick={() => void riprendi()}>
          Riprendi
        </Button>
      ) : (
        <Button size={size} variant={size ? "ghost" : undefined} disabled={busy} onClick={() => setPanel(true)}>
          Pausa
        </Button>
      )}
      {panel && <HoldPanel sessionId={session.id} onClose={() => setPanel(false)} />}
    </span>
  );
}

/**
 * «RIMUOVI CONTO BROKER», voce del menu «…» della Fase, con la conferma dentro
 * il menu (che non si chiude a un clic interno). La Fase torna «In attesa del
 * conto broker», il conto resta libero; il rifiuto del backend (coppia aperta,
 * saldo non letto) si mostra com'e' arrivato.
 */
export function RemoveBrokerAction({ sessionId, brokerLabel }: { sessionId: string; brokerLabel: string }) {
  const refresh = useRefreshDirectory();
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const rimuovi = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.sessions.removeBroker(sessionId);
      setConfirm(false);
      await refresh();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (!confirm) {
    return (
      <button type="button" role="menuitem" onClick={() => setConfirm(true)} className="block w-full rounded px-2 py-1.5 text-left text-sm text-ink hover:bg-raised">
        Rimuovi conto broker
      </button>
    );
  }
  return (
    <div className="space-y-1.5 rounded border border-line p-2">
      <p className="text-xs text-muted">
        {brokerLabel} esce dalla fase e resta libero. Con la contabilità il suo periodo si chiude sul saldo letto adesso. La fase resta «{LABELS.sessionState.AWAITING_BROKER}».
      </p>
      <Button size="sm" variant="danger" disabled={busy} onClick={() => void rimuovi()}>
        {busy ? "Rimuovo…" : "Conferma: rimuovi"}
      </Button>
      <Button size="sm" variant="ghost" disabled={busy} onClick={() => setConfirm(false)}>
        Annulla
      </Button>
      {error && (
        <p role="alert" className="text-xs text-loss">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * Importo che dipende da una conversione o da dati che possono mancare. Null
 * si mostra come trattino col motivo, un tasso assunto come «≈ · stima».
 */
export function ConvertedAmount({ value, signed = false, missingHint }: { value: { amount: number | null; currency: string; fxMissing: boolean; fxAssumed: boolean }; signed?: boolean; missingHint?: string }) {
  if (value.amount === null) {
    const why = value.fxMissing ? "Tasso di cambio broker -> prop mancante: l'importo non è calcolabile" : (missingHint ?? "Non calcolabile senza quotazioni");
    return (
      <span className="text-faint" title={why}>
        — <span className="text-xs">{value.fxMissing ? "tasso mancante" : "n.d."}</span>
      </span>
    );
  }
  return (
    <span>
      <span className={signed ? (value.amount > 0 ? "text-gain" : value.amount < 0 ? "text-loss" : "text-muted") : ""}>
        {value.fxAssumed ? "≈ " : ""}
        {money(value.amount, value.currency)}
      </span>
      {value.fxAssumed && <ApproximateNote title="Convertito a un tasso assunto (riferimento BCE), non al cambio del conto." />}
    </span>
  );
}

/**
 * Fee pagate del ciclo, in valuta del ciclo. Marcate come stima quando non
 * vengono (tutte) da righe del registro convertite col cambio del conto.
 */
export function FeesPaid({ value, currency, info }: { value: string | number; currency: string; info: { fromLedger: boolean; fxAssumed: boolean } | undefined }) {
  const estimate = !info?.fromLedger || info.fxAssumed;
  return (
    <span>
      {estimate ? "≈ " : ""}
      {money(value, currency)}
      {estimate && (
        <ApproximateNote
          title={
            !info?.fromLedger
              ? "Fee del preset senza riga nel registro: la valuta in cui è stata pagata non è verificata."
              : "Almeno una fee è convertita col riferimento BCE o registrata prima del registro."
          }
        />
      )}
    </span>
  );
}

/** Fase successiva, come la calcola il backend: lo si dice prima di confermare. */
export function nextPhaseOf(current: CyclePhase, presetPhases: number | null): CyclePhase | null {
  if (current === "FUNDED") return null;
  if (current === "PHASE_1" && (presetPhases ?? 2) >= 2) return "PHASE_2";
  return "FUNDED";
}

// ---------------------------------------------------------------------------
// Esito: fase superata / conto bruciato
// ---------------------------------------------------------------------------

/**
 * «RICALCOLA»: l'anteprima non si rifa' da sola quando cambiano i campi (ogni
 * calcolo legge il saldo sul gateway). Se i campi sono cambiati dall'ultima, lo
 * dice, e la conferma resta spenta finche' non si preme.
 */
function RecalcRow({ stale, busy, onClick }: { stale: boolean; busy: boolean; onClick: () => void }) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <Button type="button" size="sm" disabled={busy} onClick={onClick}>
        {busy ? "Calcolo…" : "Ricalcola"}
      </Button>
      {stale && !busy && <span className="text-xs text-warn">I campi sono cambiati dall'ultima anteprima: premi «Ricalcola» per poter confermare.</span>}
    </div>
  );
}

export function OutcomePanel({
  kind,
  session,
  onClose,
  onDone,
}: {
  kind: "complete" | "fail";
  session: {
    id: string;
    clientName: string;
    phase: CyclePhase;
    presetPhases: number | null;
    brokerPnl: number;
    brokerCurrency: string;
    /** Valuta del conto prop = valuta del ciclo: default della fee di fase, e destinazione del P&L del broker. */
    propCurrency?: string;
    openPairs: number;
    /** Se diversa dalla valuta del broker, la chiusura congela un tasso di cambio. */
    reportingCurrency?: string;
    /**
     * `false` = la Fase opera SENZA ciclo: niente netto, split, fee di fase,
     * rettifiche né tassi. L'esito si registra e la Fase aspetta il conto prop
     * nuovo. Assente = con contabilità, com'era.
     */
    accounting?: boolean;
  };
  onClose: () => void;
  onDone?: () => void;
}) {
  const accounting = session.accounting !== false;
  const refresh = useRefreshDirectory();
  const [fee, setFee] = useState("");
  const [adjustment, setAdjustment] = useState("");
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [fxRate, setFxRate] = useState("");
  const [feeCurrency, setFeeCurrency] = useState(session.propCurrency ?? "USD");
  const [feeRate, setFeeRate] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /*
   * L'anteprima della bruciatura, e le due quote che si confermano con lei.
   *
   * Gli importi sono lo STATO: la percentuale e' una comodita' per scriverli in
   * fretta, e quello che parte al backend sono i due numeri. Se l'operatore
   * decide che stavolta al cliente ne lascia 750 invece di 700, e' 750 che va
   * registrato — non 70% riapplicato a un netto che nel frattempo e' cambiato.
   */
  const [preview, setPreview] = useState<BurnPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [quote, setQuote] = useState<{ client: number; manager: number } | null>(null);
  /** Vero da quando l'operatore tocca un importo: da li' non si ricalcolano piu' da soli. */
  const touched = useRef(false);
  /*
   * L'anteprima si calcola all'apertura e quando si preme «Ricalcola» (12/10):
   * ogni calcolo e' una lettura del saldo sul gateway, e rifarla a ogni tasto
   * era una lettura per ogni modifica. `previewFor` e' la chiave dei campi con
   * cui e' stata fatta l'ultima (riuscita o no); se i campi sono altri, la
   * conferma resta spenta finche' non si ricalcola.
   */
  const previewKey = JSON.stringify([adjustment, fxRate]);
  const [previewFor, setPreviewFor] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const previewSeq = useRef(0);
  // Senza ciclo non c'è un netto da anteprimare (la rotta rifiuterebbe).
  const hasPreview = kind === "fail" && accounting;
  const previewStale = hasPreview && (previewing || previewFor !== previewKey);

  useEffect(() => setError(null), [kind]);

  const calcola = async () => {
    const seq = ++previewSeq.current;
    const key = previewKey;
    setPreviewing(true);
    try {
      const adj = adjustment.trim() ? decimalInput(adjustment.replace(/^-/, "")) : null;
      const rateValue = fxRate.trim() ? decimalInput(fxRate) : null;
      const p = await api.sessions.burnPreview(session.id, {
        ...(adj !== null ? { cycleAdjustment: adjustment.trim().startsWith("-") ? `-${adj}` : adj } : {}),
        ...(rateValue ? { fxRate: rateValue } : {}),
      });
      if (seq !== previewSeq.current) return;
      setPreview(p);
      setPreviewError(null);
      setPreviewFor(key);
      // I predefiniti si riallineano al netto finche' nessuno li ha toccati.
      if (!touched.current) setQuote(p.split ? { client: p.split.client, manager: p.split.manager } : null);
    } catch (e) {
      if (seq !== previewSeq.current) return;
      setPreviewError(errorMessage(e));
      setPreviewFor(key);
    } finally {
      if (seq === previewSeq.current) setPreviewing(false);
    }
  };

  useEffect(() => {
    if (hasPreview) void calcola();
    // Si chiude con la modale: un risultato che arriva dopo non scrive piu' niente.
    return () => {
      previewSeq.current++;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPreview, session.id]);

  const next = nextPhaseOf(session.phase, session.presetPhases);
  // Il P&L del broker entra nel ciclo in valuta prop: il tasso serve se le valute differiscono.
  const askRate = !!session.propCurrency && session.propCurrency !== session.brokerCurrency;
  const isComplete = kind === "complete";
  const blocked =
    session.openPairs > 0
      ? `${session.openPairs === 1 ? "C'è un'operazione aperta" : `Ci sono ${session.openPairs} operazioni aperte`}: chiudi prima ${session.openPairs === 1 ? "l'operazione" : "le operazioni"}.`
      : isComplete && !next
        ? "In funded non c'è una fase da superare: segna il conto bruciato o registra i payout."
        : null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const adj = adjustment.trim() ? decimalInput(adjustment.replace(/^-/, "")) : null;
    if (adjustment.trim() && adj === null) return setError("La rettifica deve essere un numero");
    if (adjustment.trim() && Number(adj) !== 0 && reason.trim().length < 3) return setError("Scrivi il motivo della rettifica");
    const feeValue = fee.trim() ? decimalInput(fee) : null;
    if (fee.trim() && feeValue === null) return setError("La fee deve essere un numero");
    const rateValue = fxRate.trim() ? decimalInput(fxRate) : null;
    if (fxRate.trim() && (rateValue === null || Number(rateValue) <= 0)) return setError("Il tasso deve essere un numero maggiore di zero");

    setBusy(true);
    try {
      const body = !accounting
        ? // Senza ciclo solo la nota: fee, rettifiche, tassi e split non hanno dove andare.
          { ...(note.trim() ? { note: note.trim() } : {}) }
        : {
        ...(isComplete && feeValue
          ? { nextPhaseFee: feeValue, nextPhaseFeeCurrency: feeCurrency, ...(decimalInput(feeRate) ? { nextPhaseFeeFxRate: decimalInput(feeRate)! } : {}) }
          : {}),
        ...(adj !== null ? { cycleAdjustment: adjustment.trim().startsWith("-") ? `-${adj}` : adj, adjustmentReason: reason.trim() } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
        ...(rateValue ? { fxRate: rateValue } : {}),
      };
      if (isComplete) await api.sessions.complete(session.id, body);
      else
        await api.sessions.fail(session.id, {
          ...body,
          // Gli IMPORTI, e la percentuale solo come contesto: e' quella che
          // l'operatore ha davanti, ricalcolata dai numeri che ha scritto lui.
          ...(quote ? { split: { client: quote.client, manager: quote.manager, pct: pctOf(quote.client, quote.client + quote.manager) } } : {}),
        });
      await refresh();
      onDone?.();
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <SidePanel
      open
      onClose={onClose}
      title={isComplete ? "Fase superata" : "Conto bruciato"}
      subtitle={`${session.clientName} · ${PHASE_LABEL(session.phase)}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button variant="primary" type="submit" form="outcome-form" disabled={busy || !!blocked || previewStale}>
            {busy ? "Registrazione…" : isComplete ? "Conferma fase superata" : "Conferma conto bruciato"}
          </Button>
        </>
      }
    >
      <form id="outcome-form" onSubmit={submit} className="space-y-4">
        <div className="rounded border border-line bg-raised/50 p-3 text-sm text-muted">
          {!accounting ? (
            <>
              {isComplete ? "Il prop ha superato la fase." : "Il prop è bruciato: è l'esito atteso del modello."} Questa fase opera{" "}
              <span className="text-ink">senza contabilità</span>: non c'è un ciclo, quindi nessun netto, split o recover da registrare. La
              fase si chiude e aspetta il conto prop nuovo:{" "}
              <span className="text-ink">{isComplete ? (next ? PHASE_LABEL(next) : "—") : `di nuovo ${PHASE_LABEL(session.phase)}`}</span>.
            </>
          ) : isComplete ? (
            <>
              Il prop ha superato la fase. Il P&L del broker in questa fase (
              <span className="text-ink">{money(session.brokerPnl, session.brokerCurrency)}</span>) entra nel ciclo, il ciclo passa a{" "}
              <span className="text-ink">{next ? PHASE_LABEL(next) : "—"}</span> e il valore da recuperare si ricalcola: la perdita sul reale
              si somma al recover della fase successiva.
            </>
          ) : (
            <>
              Il prop è bruciato: è l'esito atteso del modello. Il P&L del broker (
              <span className="text-ink">{money(session.brokerPnl, session.brokerCurrency)}</span>) entra nel ciclo e il ciclo si chiude.
            </>
          )}
        </div>

        {blocked && <ErrorNote>{blocked}</ErrorNote>}

        {hasPreview && <RecalcRow stale={previewFor !== previewKey} busy={previewing} onClick={() => void calcola()} />}

        {accounting && !isComplete && (
          <BurnSplit
            preview={preview}
            error={previewError}
            quote={quote}
            onQuote={(q) => {
              touched.current = true;
              setQuote(q);
            }}
          />
        )}

        {accounting && isComplete && (
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Field label="Fee della fase successiva" hint="Solo se la prop fa pagare la nuova fase. Entra nel registro, convertita.">
                {(id) => <input id={id} inputMode="decimal" value={fee} placeholder="0" onChange={(e) => setFee(e.target.value)} className={inputClass} />}
              </Field>
            </div>
            <Field label="Valuta">
              {(id) => <input id={id} value={feeCurrency} maxLength={3} onChange={(e) => setFeeCurrency(e.target.value.toUpperCase())} className={inputClass} />}
            </Field>
          </div>
        )}
        {accounting && isComplete && fee.trim() !== "" && session.reportingCurrency && feeCurrency !== session.reportingCurrency && (
          <Field label={`Tasso fee ${feeCurrency}/${session.reportingCurrency}`} hint="Vuoto = feed del broker, poi BCE. Si scrive se la registrazione lo chiede.">
            {(id) => <input id={id} inputMode="decimal" value={feeRate} placeholder="es. 0.9200" onChange={(e) => setFeeRate(e.target.value)} className={inputClass} />}
          </Field>
        )}

        {accounting && (
          <Field label="Rettifica di ciclo" hint="Spread, slippage, commissioni non catturate dal gateway. Positiva = costo, negativa = rimborso.">
            {(id) => <input id={id} inputMode="decimal" value={adjustment} placeholder="0" onChange={(e) => setAdjustment(e.target.value)} className={inputClass} />}
          </Field>
        )}
        {accounting && adjustment.trim() !== "" && Number(adjustment.replace(",", ".")) !== 0 && (
          <Field label="Motivo della rettifica">
            {(id) => <input id={id} required value={reason} onChange={(e) => setReason(e.target.value)} className={inputClass} />}
          </Field>
        )}
        {accounting && (askRate || /Tasso .* non disponibile/.test(error ?? "")) && (
          <Field
            label={`Tasso ${session.brokerCurrency}/${session.propCurrency ?? "prop"}`}
            hint="Vuoto = feed del broker, poi BCE. Si scrive solo se la chiusura lo chiede: il tasso si congela sulle coppie e non si rivaluta più."
          >
            {(id) => <input id={id} inputMode="decimal" value={fxRate} placeholder="es. 1.0850" onChange={(e) => setFxRate(e.target.value)} className={inputClass} />}
          </Field>
        )}
        <Field label="Nota">
          {(id) => <textarea id={id} rows={2} value={note} onChange={(e) => setNote(e.target.value)} className={inputClass} />}
        </Field>
        <ErrorNote>{error}</ErrorNote>
      </form>
    </SidePanel>
  );
}

/** I dati che `OutcomePanel` chiede, dal dettaglio di una Fase: una sola traduzione per la pagina e per il menu della card. */
export function outcomeSessionOf(s: SessionDetail) {
  const programma = s.preset ?? s.cycle?.preset ?? null;
  return {
    id: s.id,
    clientName: s.client.name,
    // Senza ciclo la fase e' quella della Fase stessa, e i suoi step quelli del suo programma.
    phase: s.cycle?.currentPhase ?? s.phase,
    presetPhases: programma?.phases ?? null,
    accounting: !!s.cycle,
    brokerPnl: s.summary.pnl.broker,
    brokerCurrency: s.brokerAccount?.currency ?? s.propAccount.currency,
    openPairs: s.summary.openPairs,
    reportingCurrency: s.reportingCurrency,
    propCurrency: s.propAccount.currency,
  };
}

/**
 * «CONTO BRUCIATO» (o «Fase superata») APERTO DA FUORI DALLA PAGINA DELLA FASE:
 * carica il dettaglio e monta la stessa `OutcomePanel`, anteprima compresa. Un
 * errore di caricamento sta nel riquadro, non in console.
 */
export function OutcomeFromSession({ sessionId, kind, onClose }: { sessionId: string; kind: "complete" | "fail"; onClose: () => void }) {
  const detail = useSession(sessionId);
  if (detail.data) return <OutcomePanel kind={kind} session={outcomeSessionOf(detail.data)} onClose={onClose} />;
  return (
    <SidePanel open title={kind === "fail" ? "Conto bruciato" : "Fase superata"} onClose={onClose} footer={<Button onClick={onClose}>Chiudi</Button>}>
      {detail.error ? <ErrorNote>{errorMessage(detail.error)}</ErrorNote> : <p className="text-sm text-faint">Caricamento della fase…</p>}
    </SidePanel>
  );
}

/** La voce «Conto bruciato» di un menu «…»: apre direttamente la modale, senza passare dalla pagina. */
export function BurnMenuItem({ sessionId }: { sessionId: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" role="menuitem" onClick={() => setOpen(true)} className={MENU_ITEM_CLASS}>
        {LABELS.sessionState.FAILED}
      </button>
      {open && <OutcomeFromSession sessionId={sessionId} kind="fail" onClose={() => setOpen(false)} />}
    </>
  );
}

/** Il nome del gesto: lo usano il bottone, la voce del menu e il titolo del pannello. */
export const RESET_LABEL = "Conto bruciato e reset";

/**
 * «CONTO BRUCIATO E RESET»: la prop ha azzerato il conto alla taglia con le
 * stesse credenziali. E' una bruciatura vera — Fase e ciclo si chiudono, netto e
 * split come in «Conto bruciato» — e subito dopo nasce una Fase nuova (e un ciclo
 * nuovo, se la vecchia aveva la contabilità) sugli stessi conti.
 *
 * L'anteprima e la conferma sono la stessa funzione del backend: quello che si
 * legge qui sotto e' quello che viene scritto, con il saldo riletto alla conferma.
 */
export function ResetPanel({
  session,
  onClose,
  onDone,
}: {
  session: ReturnType<typeof outcomeSessionOf>;
  onClose: () => void;
  onDone?: () => void;
}) {
  const refresh = useRefreshDirectory();
  const [fee, setFee] = useState("");
  const [feeCurrency, setFeeCurrency] = useState(session.propCurrency ?? "USD");
  const [feeRate, setFeeRate] = useState("");
  const [fxRate, setFxRate] = useState("");
  const [adjustment, setAdjustment] = useState("");
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<ResetPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [quote, setQuote] = useState<{ client: number; manager: number } | null>(null);
  /** Vero da quando l'operatore tocca un importo: da li' non si ricalcolano piu' da soli. */
  const touched = useRef(false);

  const body = (): PropResetInput => {
    const feeValue = fee.trim() ? decimalInput(fee) : null;
    const rateValue = fxRate.trim() ? decimalInput(fxRate) : null;
    const feeRateValue = feeRate.trim() ? decimalInput(feeRate) : null;
    const adj = adjustment.trim() ? decimalInput(adjustment.replace(/^-/, "")) : null;
    return {
      ...(feeValue ? { fee: feeValue, feeCurrency, ...(feeRateValue ? { feeFxRate: feeRateValue } : {}) } : {}),
      ...(rateValue ? { fxRate: rateValue } : {}),
      ...(adj !== null ? { cycleAdjustment: adjustment.trim().startsWith("-") ? `-${adj}` : adj } : {}),
    };
  };

  /*
   * L'anteprima si calcola all'apertura e con «Ricalcola», non a ogni modifica:
   * ogni calcolo legge il saldo del broker sul gateway. `previewFor` e' la chiave
   * dei campi dell'ultima anteprima; se sono cambiati, la conferma resta spenta
   * finche' non si ricalcola.
   */
  const previewKey = JSON.stringify([fee, feeCurrency, feeRate, fxRate, adjustment]);
  const [previewFor, setPreviewFor] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const previewSeq = useRef(0);

  const calcola = async () => {
    const seq = ++previewSeq.current;
    const key = previewKey;
    setPreviewing(true);
    try {
      const p = await api.sessions.resetPreview(session.id, body());
      if (seq !== previewSeq.current) return;
      setPreview(p);
      setPreviewError(null);
      setPreviewFor(key);
      if (!touched.current) setQuote(p.burn?.split ? { client: p.burn.split.client, manager: p.burn.split.manager } : null);
    } catch (e) {
      if (seq !== previewSeq.current) return;
      setPreviewError(errorMessage(e));
      setPreviewFor(key);
    } finally {
      if (seq === previewSeq.current) setPreviewing(false);
    }
  };

  useEffect(() => {
    void calcola();
    // Si chiude con la modale: un risultato che arriva dopo non scrive piu' niente.
    return () => {
      previewSeq.current++;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.id]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (fee.trim() && decimalInput(fee) === null) return setError("La fee deve essere un numero");
    if (fxRate.trim() && (decimalInput(fxRate) === null || Number(decimalInput(fxRate)) <= 0)) return setError("Il tasso deve essere un numero maggiore di zero");
    if (adjustment.trim() && decimalInput(adjustment.replace(/^-/, "")) === null) return setError("La rettifica deve essere un numero");
    const rettifica = adjustment.trim() !== "" && Number(adjustment.replace(",", ".")) !== 0;
    if (rettifica && reason.trim().length < 3) return setError("Scrivi il motivo della rettifica");
    setBusy(true);
    try {
      await api.sessions.propReset(session.id, {
        ...body(),
        ...(rettifica ? { adjustmentReason: reason.trim() } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
        // Gli IMPORTI, e la percentuale solo come contesto: come nella bruciatura.
        ...(quote ? { split: { client: quote.client, manager: quote.manager, pct: pctOf(quote.client, quote.client + quote.manager) } } : {}),
      });
      await refresh();
      onDone?.();
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const accounting = preview ? preview.accounting : true;
  const r = preview?.restart;
  const blocked =
    session.openPairs > 0
      ? `${session.openPairs === 1 ? "C'è un'operazione aperta" : `Ci sono ${session.openPairs} operazioni aperte`}: chiudi prima ${session.openPairs === 1 ? "l'operazione" : "le operazioni"}.`
      : null;
  const askFeeRate = fee.trim() !== "" && !!session.reportingCurrency && feeCurrency !== session.reportingCurrency;
  const askRate = !!preview?.burn && (preview.burn.fxMissing || preview.burn.brokerCurrency !== preview.burn.currency);
  const canConfirm = !busy && !blocked && !previewing && previewFor === previewKey && !!preview && !previewError && !preview.balanceMissing && !preview.burn?.fxMissing;
  const pct = (v: number | null) => (v === null ? "—" : `${(v * 100).toLocaleString("it-IT")}%`);

  return (
    <SidePanel
      open
      onClose={onClose}
      title={RESET_LABEL}
      subtitle={`${session.clientName} · ${PHASE_LABEL(session.phase)}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button variant="primary" type="submit" form="reset-form" disabled={!canConfirm}>
            {busy ? "Registrazione…" : "Conferma il reset"}
          </Button>
        </>
      }
    >
      <form id="reset-form" onSubmit={submit} className="space-y-4">
        <div className="rounded border border-line bg-raised/50 p-3 text-sm text-muted">
          La prop ha azzerato il conto con le stesse credenziali. Per noi è una <span className="text-ink">bruciatura vera</span>: la Fase
          {accounting ? " e il ciclo si chiudono, con netto e split" : " si chiude"}. Subito dopo riparte una{" "}
          <span className="text-ink">{PHASE_LABEL(session.phase)} nuova</span> sugli stessi conti
          {accounting ? ", con un ciclo nuovo scollegato da questo" : ""}.
        </div>

        {blocked && <ErrorNote>{blocked}</ErrorNote>}
        <RecalcRow stale={previewFor !== previewKey} busy={previewing} onClick={() => void calcola()} />
        {previewError && <ErrorNote>{previewError}</ErrorNote>}
        {!preview && !previewError && <p className="text-sm text-faint">Lettura del saldo del broker…</p>}

        {preview && accounting && (
          <BurnSplit
            preview={preview.burn}
            error={null}
            quote={quote}
            onQuote={(q) => {
              touched.current = true;
              setQuote(q);
            }}
          />
        )}

        {preview && r && (
          <dl className="space-y-3 rounded-card border border-line bg-surface p-4 text-sm">
            <div>
              <dt className="text-xs uppercase tracking-wide text-faint">Cosa nasce</dt>
              <dd className="mt-1 grid grid-cols-2 gap-x-6 gap-y-1">
                <span className="text-muted">Fase</span>
                <span className="text-ink">
                  {PHASE_LABEL(r.session.phase)} · {r.session.canonicalSymbol}
                </span>
                <span className="text-muted">Conto prop</span>
                <span className="text-ink">{r.session.propAccount.label}</span>
                <span className="text-muted">Conto broker</span>
                <span className="text-ink">{r.session.brokerAccount.label}</span>
                <span className="text-muted">Taglia · DD massimo</span>
                <span className="tabular-nums text-ink">
                  {money(r.session.accountSize, r.session.propAccount.currency)} · {pct(r.session.maxDdPct)}
                </span>
                <span className="text-muted">Target di fase · DD giornaliero</span>
                <span className="tabular-nums text-ink">
                  {pct(r.session.challengeTargetPct)} · {pct(r.session.dailyDdPct)}
                </span>
              </dd>
              {r.session.usesLastBalance && (
                <p className="mt-2 text-xs text-faint">
                  Con questa base il DD massimo legge l'ultimo saldo noto del prop, che resta quello di prima finché non premi «Connetti» sul conto.
                </p>
              )}
            </div>
            {r.cycle && (
              <div>
                <dt className="text-xs uppercase tracking-wide text-faint">Ciclo nuovo</dt>
                <dd className="mt-1 grid grid-cols-2 gap-x-6 gap-y-1">
                  <span className="text-muted">Etichetta</span>
                  <span className="text-ink">{r.cycle.label ?? "—"}</span>
                  <span className="text-muted">Fee del reset</span>
                  <span className="tabular-nums text-ink">{r.cycle.fee.amount > 0 ? money(r.cycle.fee.amount, r.cycle.fee.currency) : "nessuna"}</span>
                  <span className="text-muted">Da recuperare</span>
                  <span className="tabular-nums text-ink">
                    {r.cycle.recoverAmount === null ? "la sola fee, convertita alla conferma" : money(r.cycle.recoverAmount, r.cycle.currency)}
                  </span>
                  <span className="text-muted">Split (predefinito del cliente)</span>
                  <span className="tabular-nums text-ink">
                    payout {pct(r.cycle.payoutSplitPct)} · bruciatura {pct(r.cycle.burnSplitPct)}
                  </span>
                </dd>
              </div>
            )}
            {r.period && (
              <div>
                <dt className="text-xs uppercase tracking-wide text-faint">Primo periodo del ciclo nuovo</dt>
                <dd className="mt-1 grid grid-cols-2 gap-x-6 gap-y-1">
                  <span className="text-muted">Base (misurata{r.period.baseAt ? `, letta alle ${dateTime(r.period.baseAt)}` : ""})</span>
                  <span className="tabular-nums text-ink">{r.period.baseAmount === null ? "—" : money(r.period.baseAmount, r.period.currency)}</span>
                </dd>
              </div>
            )}
          </dl>
        )}

        {accounting && (
          <>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Field label="Fee del reset" hint="Facoltativa. È la fee del ciclo nuovo, nota «Fee reset»; il «Da recuperare» parte da qui.">
                  {(id) => <input id={id} inputMode="decimal" value={fee} placeholder="0" onChange={(e) => setFee(e.target.value)} className={inputClass} />}
                </Field>
              </div>
              <Field label="Valuta">
                {(id) => <input id={id} value={feeCurrency} maxLength={3} onChange={(e) => setFeeCurrency(e.target.value.toUpperCase())} className={inputClass} />}
              </Field>
            </div>
            {askFeeRate && (
              <Field label={`Tasso fee ${feeCurrency}/${session.reportingCurrency}`} hint="Vuoto = feed del broker, poi BCE. Si scrive se la registrazione lo chiede.">
                {(id) => <input id={id} inputMode="decimal" value={feeRate} placeholder="es. 0.9200" onChange={(e) => setFeeRate(e.target.value)} className={inputClass} />}
              </Field>
            )}
            <Field label="Rettifica del ciclo che si chiude" hint="Spread, slippage, commissioni non catturate. Positiva = costo, negativa = rimborso.">
              {(id) => <input id={id} inputMode="decimal" value={adjustment} placeholder="0" onChange={(e) => setAdjustment(e.target.value)} className={inputClass} />}
            </Field>
            {adjustment.trim() !== "" && Number(adjustment.replace(",", ".")) !== 0 && (
              <Field label="Motivo della rettifica">
                {(id) => <input id={id} required value={reason} onChange={(e) => setReason(e.target.value)} className={inputClass} />}
              </Field>
            )}
          </>
        )}
        {(askRate || /Tasso .* non disponibile/.test(error ?? "")) && (
          <Field
            label={`Tasso ${session.brokerCurrency}/${session.propCurrency ?? "prop"}`}
            hint="Vuoto = feed del broker, poi BCE. Si congela sul periodo che si chiude e non si rivaluta più."
          >
            {(id) => <input id={id} inputMode="decimal" value={fxRate} placeholder="es. 1.0850" onChange={(e) => setFxRate(e.target.value)} className={inputClass} />}
          </Field>
        )}
        <Field label="Nota">
          {(id) => <textarea id={id} rows={2} value={note} onChange={(e) => setNote(e.target.value)} className={inputClass} />}
        </Field>
        <ErrorNote>{error}</ErrorNote>
      </form>
    </SidePanel>
  );
}

/** Il reset aperto da fuori dalla pagina della Fase: carica il dettaglio e monta lo stesso pannello. */
function ResetFromSession({ sessionId, onClose }: { sessionId: string; onClose: () => void }) {
  const detail = useSession(sessionId);
  if (detail.data) return <ResetPanel session={outcomeSessionOf(detail.data)} onClose={onClose} />;
  return (
    <SidePanel open title={RESET_LABEL} onClose={onClose} footer={<Button onClick={onClose}>Chiudi</Button>}>
      {detail.error ? <ErrorNote>{errorMessage(detail.error)}</ErrorNote> : <p className="text-sm text-faint">Caricamento della fase…</p>}
    </SidePanel>
  );
}

/** La voce «Conto bruciato e reset» di un menu «…», accanto a `BurnMenuItem`. */
export function ResetMenuItem({ sessionId }: { sessionId: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" role="menuitem" onClick={() => setOpen(true)} className={MENU_ITEM_CLASS}>
        {RESET_LABEL}
      </button>
      {open && <ResetFromSession sessionId={sessionId} onClose={() => setOpen(false)} />}
    </>
  );
}

/** La percentuale che i due importi realizzano, come frazione. */
export function pctOf(part: number, total: number): string {
  if (!(total > 0)) return "0";
  return String(Math.round((part / total) * 1_000_000) / 1_000_000);
}

const r2 = (n: number) => Math.round(n * 100) / 100;

/**
 * Il netto del ciclo e le due quote, prima di confermare la bruciatura.
 *
 * Tre regole, e sono le stesse in tutto il pannello:
 *
 * 1. **ogni importo porta la sua valuta**, che e' quella del ciclo e non si
 *    deduce da nessuna parte: un numero nudo qui vale quanto un numero
 *    sbagliato;
 * 2. **la percentuale sta accanto al suo importo**, mai da sola: da sola non
 *    dice quanti soldi sono, ed e' l'importo che si registra;
 * 3. **se manca il tasso non si mostra un netto**: si dice che manca. Una cifra
 *    stimata qui sarebbe la cifra su cui qualcuno firma uno split.
 */
function BurnSplit({
  preview,
  error,
  quote,
  onQuote,
}: {
  preview: BurnPreview | null;
  error: string | null;
  quote: { client: number; manager: number } | null;
  onQuote: (q: { client: number; manager: number } | null) => void;
}) {
  if (error) return <ErrorNote>{error}</ErrorNote>;
  if (!preview) return <p className="text-sm text-faint">Calcolo del netto del ciclo…</p>;

  const cur = preview.currency;
  const net = preview.cycleNet;

  /*
   * IL SALDO CHE NON SI E' LETTO, detto qui e non dopo il rifiuto.
   *
   * Il netto del ciclo si misura sulla differenza di saldo del conto broker:
   * senza quel numero non c'e' un netto da mostrare, e la chiusura si
   * rifiutera' con lo stesso messaggio. Meglio saperlo prima di compilare.
   */
  const saldo = preview.balanceProblem && (
    <div className="rounded border border-warn/40 bg-warn/5 p-3 text-sm text-muted">
      {sentence(preview.balanceProblem.message)} Il netto del tratto si misura sul saldo letto a posizioni chiuse: finché
      non si legge, la chiusura non registra niente.
    </div>
  );

  /** Un saldo letto da piu' di 15 minuti si dice: il netto del ciclo si misura su di lui. */
  const letturaVecchia = !!preview.period?.closeAt && Date.now() - new Date(preview.period.closeAt).getTime() > 15 * 60_000;
  const vecchio = letturaVecchia && preview.period?.closeAt && (
    <div className="rounded border border-warn/40 bg-warn/5 p-3 text-sm text-muted">
      Il saldo del broker è stato letto alle {dateTime(preview.period.closeAt)}, più di 15 minuti fa: il netto potrebbe non essere aggiornato.
    </div>
  );

  if (preview.fxMissing) {
    return (
      <>
        {saldo}
        <div className="rounded border border-warn/40 bg-warn/5 p-3 text-sm text-muted">
          Manca il tasso {preview.fx?.base}/{preview.fx?.quote}: il P&L del broker è{" "}
          <span className="text-ink">{money(preview.brokerPnlNative, preview.brokerCurrency)}</span>, ma senza tasso non diventa un netto in{" "}
          {cur}. Scrivi il tasso qui sotto e il netto compare.
        </div>
      </>
    );
  }

  return (
    <div className="space-y-3 rounded border border-line bg-raised/50 p-3">
      {saldo}
      {vecchio}
      <dl className="space-y-1 text-sm">
        {/*
          * IL NETTO DEL TRATTO, dal saldo: è il numero che la chiusura
          * scriverà. Il P&L dai deal resta sotto, ed è un'altra misura.
          */}
        {preview.period && (
          <Riga
            label={`Netto del tratto n. ${preview.period.seq}`}
            value={
              preview.period.netCycle === null
                ? "—"
                : `${money(preview.period.netCycle, cur)}${
                    preview.period.currency === cur || preview.period.netNative === null
                      ? ""
                      : ` · ${money(preview.period.netNative, preview.period.currency)}`
                  }`
            }
          />
        )}
        {preview.period?.closeAt && (
          <Riga label="Saldo del broker letto" value={dateTime(preview.period.closeAt)} />
        )}
        <Riga label="P&L del broker in questa fase" value={money(preview.brokerPnl, cur)} />
        <Riga label="P&L realizzato sul reale" value={money(preview.realRealizedPnl, cur)} />
        <Riga label="Payout incassati" value={money(preview.payoutsReceived, cur)} />
        <Riga label="Fee pagate" value={money(-preview.feesPaid, cur)} />
        <Riga label="Rettifiche" value={money(-preview.adjustments, cur)} />
        {/* Inseriti a mano, valgono come legge: si vedono separati dal calcolato. */}
        {preview.cycleNetRebalances !== 0 && <Riga label="Ribilanciamenti (a mano)" value={money(preview.cycleNetRebalances, cur)} />}
        <div className="flex items-baseline justify-between border-t border-line pt-1">
          <dt className="text-ink">Netto del ciclo</dt>
          <dd className="font-medium text-ink">
            {money(net, cur)}
            {preview.cycleNetRebalances !== 0 && <span className="block text-right text-xs font-normal text-faint">di cui ribilanciamenti {money(preview.cycleNetRebalances, cur)}</span>}
          </dd>
        </div>
      </dl>

      {net === null || net <= 0 ? (
        <p className="text-xs text-faint">
          Niente da dividere: lo split nasce da un netto positivo. Il ciclo si chiude lo stesso, e il netto resta scritto su di lui.
        </p>
      ) : (
        <SplitEditor net={net} currency={cur} quote={quote} defaultPct={preview.burnSplitPct} onQuote={onQuote} />
      )}
    </div>
  );
}

/**
 * LE DUE QUOTE DI UN NETTO, modificabili. Una sola implementazione per le due
 * strade che generano uno split — la bruciatura e il payout: due copie
 * divergerebbero il giorno in cui una impara qualcosa che l'altra non sa.
 *
 * Gli IMPORTI sono lo stato, la percentuale è una comodità per scriverli in
 * fretta: se l'operatore decide che stavolta al cliente ne lascia 750 invece di
 * 700, è 750 che va registrato, non 70% riapplicato a un netto che nel
 * frattempo è cambiato. Le due quote sommano sempre al netto, o il backend
 * rifiuta (`SPLIT_MISMATCH`).
 */
export function SplitEditor({
  net,
  currency,
  quote,
  defaultPct,
  onQuote,
}: {
  net: number;
  currency: string;
  quote: { client: number; manager: number } | null;
  /** La percentuale CONGELATA sul ciclo: il punto di partenza, non un vincolo. */
  defaultPct: number;
  onQuote: (q: { client: number; manager: number }) => void;
}) {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-[1fr_auto] gap-2">
        <QuotaField
          label="Quota cliente"
          currency={currency}
          value={quote?.client ?? 0}
          pct={pctOf(quote?.client ?? 0, net)}
          max={net}
          onChange={(v) => onQuote({ client: r2(v), manager: r2(net - v) })}
        />
        <PctField
          value={quote ? (quote.client / net) * 100 : defaultPct * 100}
          onChange={(p) => {
            const client = r2((net * p) / 100);
            onQuote({ client, manager: r2(net - client) });
          }}
        />
      </div>
      <QuotaField
        label="Quota nostra"
        currency={currency}
        value={quote?.manager ?? 0}
        pct={pctOf(quote?.manager ?? 0, net)}
        max={net}
        onChange={(v) => onQuote({ client: r2(net - v), manager: r2(v) })}
      />
      <p className="text-xs text-faint">
        Si registrano gli IMPORTI, non la percentuale: {percent(Number(pctOf(quote?.client ?? 0, net)), 1)} è quello che questi numeri
        realizzano su {money(net, currency)}, ed è lì per farli scrivere in fretta. Le due quote sommano sempre al netto.
      </p>
    </div>
  );
}

function Riga({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between">
      <dt className="text-muted">{label}</dt>
      <dd className="text-ink">{value}</dd>
    </div>
  );
}

/**
 * Un campo numerico che mentre lo si scrive resta quello che si sta scrivendo.
 *
 * Il valore vero e' un numero e arriva da fuori — l'altro campo lo muove — ma
 * il testo digitato e' suo finche' il campo ha il fuoco: rimandare indietro
 * `String(numero)` a ogni tasto cancella lo «0,» appena battuto e non si riesce
 * piu' a scrivere un decimale.
 */
function NumberInput({ id, value, max, onChange, className }: { id: string; value: number; max?: number; onChange: (v: number) => void; className?: string }) {
  const [txt, setTxt] = useState(String(value));
  const [scrivo, setScrivo] = useState(false);
  useEffect(() => {
    if (!scrivo) setTxt(String(value));
  }, [value, scrivo]);
  return (
    <input
      id={id}
      inputMode="decimal"
      value={txt}
      onFocus={() => setScrivo(true)}
      onBlur={() => setScrivo(false)}
      onChange={(e) => {
        setTxt(e.target.value);
        const n = Number(e.target.value.replace(",", "."));
        if (Number.isFinite(n) && n >= 0 && (max === undefined || n <= max)) onChange(n);
      }}
      className={className ?? inputClass}
    />
  );
}

/** Un importo con la sua valuta scritta accanto, e la percentuale che realizza. */
function QuotaField({
  label,
  currency,
  value,
  pct,
  max,
  onChange,
}: {
  label: string;
  currency: string;
  value: number;
  pct: string;
  max: number;
  onChange: (v: number) => void;
}) {
  return (
    <Field label={`${label} (${currency})`} hint={`${percent(Number(pct), 1)} del netto`}>
      {(id) => <NumberInput id={id} value={value} max={max} onChange={onChange} />}
    </Field>
  );
}

function PctField({ value, onChange }: { value: number; onChange: (p: number) => void }) {
  return (
    <Field label="%" hint="al cliente">
      {(id) => <NumberInput id={id} value={Math.round(value * 100) / 100} max={100} onChange={onChange} className={`${inputClass} w-24`} />}
    </Field>
  );
}
